// React component that renders stop notes and toggles them

import React, { useEffect, useState } from 'react';
import TwField, { TwInputTypes } from '../../TwFields/TwFields';
import TwCheckbox from '../../TwCheckboxes/TwCheckboxes';
import { Theme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { PatternFormat } from 'react-number-format';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import useMediaQuery from '@mui/material/useMediaQuery';

interface StopNoteContactProps {
  /*
   * Boolean value that determines whether to show notes
   */
  isNoteVisible: boolean;
  hasLocationBeenUpdated: boolean;

  /*
   * callback function to toggle notes
   */
  toggleNoteVisibility: () => void;
  /**
   * initial values if location comes with them
   */
  initialNote: string;
  initialName: string;
  initialPhone: string;
  initialExt: string;
  initialEmail: string;
  /*
   * callback function to set values
   */
  handleFieldChange: (value: string, field: string) => void;
  handleLocationContactChange: (value: string, field: string) => void;
  isImportLoad?: boolean;
  disableCheckbox: boolean;
}

const StopNoteContactInfo: React.FC<StopNoteContactProps> = ({
  isNoteVisible,
  hasLocationBeenUpdated,
  toggleNoteVisibility,
  handleFieldChange,
  handleLocationContactChange,
  initialEmail,
  initialExt,
  initialName,
  initialNote,
  initialPhone,
  isImportLoad,
  disableCheckbox
}): JSX.Element => {
  const [note, setNote] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactExt, setContactExt] = useState('');
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  useEffect(() => {
    if (isNoteVisible) {
      setNote(initialNote);
      setContactEmail(initialEmail);
      setContactName(initialName);
      setContactPhone(initialPhone);
      setContactExt(initialExt);
    }
  }, [
    isNoteVisible,
    hasLocationBeenUpdated,
    initialEmail,
    initialExt,
    initialName,
    initialNote,
    initialPhone
  ]);

  return (
    <Stack direction="column" spacing={1}>
      <TwCheckbox
        name="locationNotesVisible"
        label={`Include location notes and/or contact`}
        checked={isNoteVisible}
        onInputChange={toggleNoteVisibility}
        disabled={disableCheckbox}
      />
      {isNoteVisible && (
        <Stack direction="column" spacing={1}>
          <Typography variant="body2" fontWeight="500" color="#6B7280" fontSize="14px">
            Location notes
          </Typography>

          <TwField
            id="locationNotes"
            name="locationNotes"
            inputType={TwInputTypes.textarea}
            onInputBlur={(e): void => {
              handleFieldChange(e.target.value, 'notes');
            }}
            value={note}
            onInputChange={(e): void => {
              setNote(e.target.value);
            }}
            disabled={disableCheckbox}
          />

          <Box sx={{ width: isImportLoad || matchesMobileOrTablet ? '100%' : '50%' }}>
            <TwField
              id="locationContact"
              name="contact_name"
              inputType={TwInputTypes.text}
              label="Contact name"
              fullWidth
              value={contactName}
              onInputBlur={(e): void => {
                handleLocationContactChange(e.target.value, 'contact_name');
              }}
              onInputChange={(e): void => {
                setContactName(e.target.value);
              }}
              disabled={disableCheckbox}
            />
          </Box>

          <Stack
            flexDirection={isImportLoad || matchesMobileOrTablet ? 'column' : 'row'}
            justifyContent={'space-between'}
            gap={1}
            className="stop__location-contact-fields-v2"
          >
            <TwField
              className={`stop__location-contact-email-field${
                isImportLoad || matchesMobileOrTablet ? '-mobile' : ''
              }`}
              id="locationContactEmail"
              name="contact_email"
              inputType={TwInputTypes.text}
              label="Email"
              value={contactEmail}
              onInputBlur={(e): void => {
                handleLocationContactChange(e.target.value, 'contact_email');
              }}
              onInputChange={(e): void => {
                setContactEmail(e.target.value);
              }}
              disabled={disableCheckbox}
            />
            <Stack className="stop__contact-phone-container" flexDirection={'row'}>
              <PatternFormat
                format="(###) ###-####"
                id="phone"
                name="contact_number"
                value={contactPhone}
                placeholder="(___) ___-____"
                customInput={TwField}
                onInputBlur={(e: any): void => {
                  handleLocationContactChange(e.target.value, 'contact_number');
                }}
                onInputChange={(e: any): void => {
                  setContactPhone(e.target.value);
                }}
                displayType="input"
                label="Phone"
                className="stop__contact-phone-field"
                disabled={disableCheckbox}
              />

              <TwField
                className="stop__contact-phone-ext-field"
                id="locationContactExt"
                name="contact_ext"
                inputType={TwInputTypes.text}
                placeholder="Ext."
                value={contactExt}
                onInputBlur={(e): void => {
                  handleLocationContactChange(e.target.value, 'contact_number_ext');
                }}
                onInputChange={(e): void => {
                  setContactExt(e.target.value);
                }}
                disabled={disableCheckbox}
              />
            </Stack>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default StopNoteContactInfo;
